import React, { memo, useCallback } from 'react';

import { Alert, App, Modal, Typography } from 'antd';

import { Formik, Form } from 'formik';
import type { FormikProps } from 'formik';
import { functions } from 'helpers';
import type { IAffiliate } from 'v2/interfaces/affiliate';
import affiliateService from 'v2/services/affiliate';

import FormCommision from './FormCommision';
import type { IFormCommision } from './interfaces';
import validationSchema from './validations';

interface IProps {
  open: boolean;
  handleClose: (...args: any[]) => any;
  affiliate: IAffiliate | null;
}

const InitialValues = {
  active: false,
  type: 'P',
  value: ''
};

const CustomCommissionForm = ({ open, handleClose, affiliate }: IProps) => {
  const { message, modal } = App.useApp();
  const onSubmit = useCallback(
    async (values: IFormCommision, { setSubmitting }: FormikProps<IFormCommision>) => {
      if (!affiliate) {
        return;
      }

      setSubmitting(true);

      if (!values.active) {
        affiliateService.configCustomCommission(affiliate.id).subscribe(
          () => {
            message.success('Comissão diferenciada configurada com sucesso!');
            handleClose();
          },
          () => {
            message.error(
              'Erro ao configurar comissão, verifique se o valor da comissão não é maior que o valor do produto'
            );
          },
          () => {
            setSubmitting(false);
          }
        );
        return;
      }

      if (values.active && !values.value) {
        message.error(`Insira ${values.type === 'P' ? 'a porcentagem' : 'o valor'}`);
        setSubmitting(false);
        return;
      }

      const cleanValue = Number.parseFloat(functions.clearMoneyMask(values.value));

      if (values.type === 'V') {
        const confirmation = await new Promise<boolean>(resolve => {
          modal.confirm({
            title: 'Atenção',
            content:
              'Verifique se há cupons de desconto cadastrado para esse produto. Caso a comissão diferenciada seja maior que o valor do produto com desconto o pagamento de seus afiliados e coprodutores será afetado.',
            okText: 'Salvar comissão',
            cancelText: 'Cancelar',
            cancelButtonProps: { type: 'text' },
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });

        if (!confirmation) {
          return;
        }
      }

      affiliateService.configCustomCommission(affiliate.id, values.type, cleanValue).subscribe(
        () => {
          message.success('Comissão diferenciada configurada com sucesso!');
          handleClose();
        },
        () => {
          message.error(
            'Erro ao configurar comissão, verifique se o valor da comissão não é maior que o valor do produto'
          );
        },
        () => {
          setSubmitting(false);
        }
      );
    },
    [affiliate, handleClose, message, modal]
  );

  return (
    <Modal
      open={open}
      destroyOnClose
      onCancel={() => handleClose()}
      title='Comissão diferenciada'
      footer={null}
      width={640}
    >
      <div className='flex flex-col gap-4'>
        <Alert
          type='info'
          showIcon
          message={
            <Typography.Text>
              Ao habilitar esta opção a comissão do afiliado selecionado irá sobrepor as configurações pré-definidas no
              cadastro do seu produto.
            </Typography.Text>
          }
        />

        {open && affiliate && (
          <Formik
            key={'form-commision-' + affiliate.id}
            initialValues={InitialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {() => (
              <Form>
                <FormCommision affiliate={affiliate} handleClose={handleClose} />
              </Form>
            )}
          </Formik>
        )}
      </div>
    </Modal>
  );
};

export default memo(CustomCommissionForm);
